// src/viewmodels/useCustomer360ViewModel.ts
import { useEffect, useRef, useState } from "react";
import { models, factories, service } from "powerbi-client";
import { getCookie } from "../../utils/decrypt";
import { userService } from "../../services/userService";
import logger from "../../utils/logger";
import { VISUAL_KEY } from "../../constant/appConsatnt";
import { POWER_BI_ACTIVE_TAB_OPTIONS } from "../../constant/enum";

// Helper function to create customer filter
const createCustomerFilter = (customerId: string) => ({
  $schema: "http://powerbi.com/product/schema#basic",
  target: {
    table: "Customer_Dim",
    column: "Id"
  },
  operator: "In",
  values: [customerId],
  filterType: models.FilterType.Basic
});

// Helper function to set active page
const setActivePage = async (report: any, pageName: string) => {
  try {
    const pages = await report.getPages();
    const targetPage = pages.find((page: any) =>
      page.displayName.toLowerCase().includes(pageName.toLowerCase())
    );
    
    if (targetPage) {
      await targetPage.setActive();
      return true;
    }
    return false;
  } catch (err) {
    logger.error(`setActivePage - ${pageName}`, err);
    return false;
  }
};

// Helper function to update slicer visual
const updateSlicerVisual = async (report: any, customerId: string) => {
  try {
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const pages = await report.getPages();
    const activePage = pages[0];
    const visuals = await activePage.getVisuals();
    const slicerVisual = visuals.find((v: any) => v.name === VISUAL_KEY);
    
    if (slicerVisual) {
      await slicerVisual.setSlicerState({
        filters: [{
          $schema: "http://powerbi.com/product/schema#basic",
          target: {
            table: "Customer_Dim",
            column: "Id"
          },
          operator: "In",
          values: [customerId],
          requireSingleSelection: false
        }]
      });
      return true;
    }
    return false;
  } catch (err) {
    logger.warn("updateSlicerVisual", err);
    return false;
  }
};

export default function Customer360ViewModel() {
  const reportRef = useRef<any>(null);
  const powerbiService = useRef(
    new service.Service(
      factories.hpmFactory,
      factories.wpmpFactory,
      factories.routerFactory
    )
  );

  const [embedData, setEmbedData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [reportInstance, setReportInstance] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<POWER_BI_ACTIVE_TAB_OPTIONS>(POWER_BI_ACTIVE_TAB_OPTIONS.PATTERNS);
  const filterAppliedRef = useRef<boolean>(false); // Track if filter has been applied

  // Get customer ID from cookie
  useEffect(() => {
    const idFromCookie = getCookie("customerId");
    setCustomerId(idFromCookie || "10002");
  }, []);

  // Set default tab for mobile
  useEffect(() => {
    if (window.innerWidth < 768) {
      setActiveTab(POWER_BI_ACTIVE_TAB_OPTIONS.PATTERNS);
    }
  }, []);

  // Fetch Power BI embed info
  useEffect(() => {
    const fetchEmbedData = async () => {
      try {
        const response: any = await userService.getembedinfo();
        const data = response;
        
        if (!data || !data.accessToken || !data.embedUrl || !data.reportId) {
          setError("Invalid embed data received from API");
          return;
        }
        
        setEmbedData(data);
        
        if (!customerId && data.customerId) {
          setCustomerId(data.customerId);
        }
      } catch (err: any) {
        setError(`Failed to fetch Power BI data: ${err?.message || 'Unknown error'}`);
        logger.error(`${Customer360ViewModel.name} - fetchEmbedData`, err);
      }
    };
    fetchEmbedData();
  }, [customerId]);

  // Embed Power BI report
  useEffect(() => {
    if (!embedData || !reportRef.current || !customerId) return;

    filterAppliedRef.current = false;
    powerbiService.current.reset(reportRef.current);

    const reportConfig = {
      type: "report",
      id: embedData.reportId,
      embedUrl: embedData.embedUrl,
      accessToken: embedData.accessToken,
      tokenType: models.TokenType.Embed,
      filters: [createCustomerFilter(customerId)],
      settings: {
        background: models.BackgroundType.Transparent,
        layoutType: models.LayoutType.Custom,
        customLayout: {
          displayOption: models.DisplayOption.FitToPage,
        },
        panes: {
          filters: { visible: false },
          pageNavigation: { visible: true },
        },
      },
    };

    const report = powerbiService.current.embed(reportRef.current, reportConfig) as any;
    setReportInstance(report);


    report.on("loaded", async () => {
      await setActivePage(report, "pattern");
    });

    report.on("rendered", async () => {
      if (filterAppliedRef.current) return;
      
      filterAppliedRef.current = true;
      await updateSlicerVisual(report, customerId);
    });

    report.on("error", (event: any) => {
      if (event?.detail?.errorCode === "403" || event?.detail?.errorCode === "401") {
        setError("Authentication error loading report.");
      }
      logger.error("PowerBI Report Error", event?.detail);
    });
  }, [embedData, customerId]);

  // Page switch logic
  useEffect(() => {
    const switchPageAndReapplyFilter = async () => {
      if (!reportInstance || !customerId) return;
      try {
        const pages = await reportInstance.getPages();
        const targetPage = pages.find((page: any) =>
          activeTab === POWER_BI_ACTIVE_TAB_OPTIONS.PATTERNS
            ? page.displayName.toLowerCase().includes("pattern")
            : page.displayName.toLowerCase().includes("health")
        );
        if (targetPage) {
          await targetPage.setActive();
          // Re-apply customer filter after switching page
          await reportInstance.updateFilters(models.FiltersOperations.Replace, [createCustomerFilter(customerId)]);
          // Update slicer visual to reflect customerId
          await updateSlicerVisual(reportInstance, customerId);
        } else {
          logger.warn("Page not found:", activeTab);
        }
      } catch (err) {
        logger.error(`${Customer360ViewModel.name} - switchPage`, err);
      }
    };
    switchPageAndReapplyFilter();
  }, [activeTab, reportInstance, customerId]);

  return {
    reportRef,
    embedData,
    customerId,
    error,
    activeTab,
    setActiveTab,
  };
}
